import type { ApiResult } from "admin/api/api_result";
import {
  fetchMapping,
  getAgglomeratesForDatasetLayer,
  getAgglomeratesForSegmentsFromDatastore,
  getAgglomeratesForSegmentsFromTracingstore,
  getMappingsForDatasetLayer,
} from "admin/rest_api";
import { message } from "antd";
import { toBigInt } from "libs/bigint_helpers";
import ErrorHandling from "libs/error_handling";
import Toast from "libs/toast";
import { fastDiffSetAndMap, sleep } from "libs/utils";
import min from "lodash-es/min";
import { buffers, eventChannel } from "redux-saga";
import {
  actionChannel,
  all,
  call,
  cancel,
  flush,
  fork,
  put,
  race,
  take,
  takeEvery,
  takeLatest,
} from "typed-redux-saga";
import type { APIDataLayer, APIDataset, APIMapping } from "types/api_types";
import { MappingStatusEnum, type MappingType } from "viewer/constants";
import { getSegmentIdForPositionAsync } from "viewer/controller/combinations/volume_handlers";
import {
  getLayerByName,
  getMappingInfo,
  getSegmentationLayers,
  getVisibleSegmentationLayer,
} from "viewer/model/accessors/dataset_accessor";
import { AnnotationTool } from "viewer/model/accessors/tool_accessor";
import {
  type BucketRetrievalSource,
  getActiveCellId,
  getActiveSegmentationTracingLayer,
  getBucketRetrievalSourceFn,
  getEditableMappingForVolumeTracingId,
  needsLocalHdf5Mapping as getNeedsLocalHdf5Mapping,
  getVolumeTracingByLayerName,
  isMappingActivationAllowed,
  isZoomThresholdExceededForAgglomerateMapping,
} from "viewer/model/accessors/volumetracing_accessor";
import {
  type EnsureLayerMappingsAreLoadedAction,
  setLayerMappingsAction,
} from "viewer/model/actions/dataset_actions";
import { snapshotMappingDataForNextRebaseAction } from "viewer/model/actions/save_actions";
import type {
  OptionalMappingProperties,
  SetMappingAction,
  SetMappingDataAction,
} from "viewer/model/actions/settings_actions";
import {
  clearMappingAction,
  finishMappingInitializationAction,
  setMappingAction,
  setMappingDataAction,
} from "viewer/model/actions/settings_actions";
import {
  MAPPING_MESSAGE_KEY,
  setCacheResultForDiffMappings,
} from "viewer/model/bucket_data_handling/mappings";
import type { Saga } from "viewer/model/sagas/effect_generators";
import { select } from "viewer/model/sagas/effect_generators";
import { jsHsv2rgb } from "viewer/shaders/utils.glsl";
import { api, Model } from "viewer/singletons";
import type {
  ActiveMappingInfo,
  EditableMapping,
  Mapping,
  NumberLike,
  NumberLikeMap,
  StoreAnnotation,
} from "viewer/store";
import { setActiveCellAction, updateSegmentAction } from "../../actions/volumetracing_actions";
import type DataCube from "../../bucket_data_handling/data_cube";
import { listenToStoreProperty } from "../../helpers/listener_helpers";
import { ensureWkInitialized } from "../ready_sagas";
import { waitUntilNoActiveOperations } from "../saga_helpers";

type APIMappings = Record<string, APIMapping>;
type Container<T> = { value: T };

const BUCKET_WATCHING_THROTTLE_DELAY = import.meta.env.MODE === "test" ? 5 : 50;

const takeLatestMappingChange = (
  oldActiveMappingByLayer: Container<Record<string, ActiveMappingInfo>>,
  layerName: string,
) => {
  return fork(function* () {
    let lastWatcherTask;
    let lastBucketRetrievalSource;

    const bucketRetrievalSourceChannel = createBucketRetrievalSourceChannel(layerName);
    const getBucketRetrievalSourceForLayer = getBucketRetrievalSourceFn(layerName);
    while (true) {
      lastBucketRetrievalSource = yield* select((state) => getBucketRetrievalSourceForLayer(state));
      const bucketRetrievalSource = yield* take(bucketRetrievalSourceChannel);

      const activeMappingByLayer = yield* select(
        (state) => state.temporaryConfiguration.activeMappingByLayer,
      );
      const mapping = getMappingInfo(activeMappingByLayer, layerName);

      if (process.env.NODE_ENV === "production") {
        console.log("Changed from", lastBucketRetrievalSource, "to", bucketRetrievalSource);
      }

      if (lastWatcherTask) {
        console.log("Cancel old bucket watcher");
        yield cancel(lastWatcherTask);
        lastWatcherTask = null;
      }

      // Changing between REQUESTED-WITH-MAPPING <> REQUESTED-WITHOUT-MAPPING
      if (lastBucketRetrievalSource[0] !== bucketRetrievalSource[0]) {
        yield* call(reloadData, oldActiveMappingByLayer, { layerName });
      }

      const needsLocalHdf5Mapping = yield* select((state) =>
        getNeedsLocalHdf5Mapping(state, layerName),
      );
      if (needsLocalHdf5Mapping) {
        // Start a new watcher
        console.log("Start new bucket watcher for layer", layerName);
        lastWatcherTask = yield* fork(watchChangedBucketsForLayer, layerName);
      } else if (
        lastBucketRetrievalSource[0] === "REQUESTED-WITHOUT-MAPPING" &&
        lastBucketRetrievalSource[1] === "LOCAL-MAPPING-APPLIED" &&
        mapping.mappingType !== "JSON"
      ) {
        // needsLocalHdf5Mapping is false, but in the last iteration, a local mapping
        // was applied. In case of a HDF5 mapping, this means that the mapping should
        // now be applied by the back-end. We have to clear the mapping so that
        // the data from the back-end is not mapped again.
        yield* put(clearMappingAction(layerName));
      }
    }
  });
};

// There are two distinct ways the active mapping ends up in the store. Keeping them apart matters:
//
// (1) ACTIVATING a mapping is always a TWO-PHASE process:
//      Phase 1: setMappingAction configures which mapping becomes active (its name + type). It does
//               NOT carry the mapping data. Normally the mapping saga reacts to it, loads the data
//               from the server and dispatches setMappingDataAction itself. If the caller already
//               holds the data (front-end API / merger mode supplying a custom mapping under the
//               synthetic name "<custom mapping>"), it passes dataIsProvidedExternally so the saga
//               skips loading and the caller dispatches setMappingDataAction itself.
//      Phase 2: setMappingDataAction carries the actual mapping data (the Map). It triggers the
//               texture update and finishMappingInitializationAction (status ACTIVATING -> ENABLED).
//
// (2) UPDATING the data of an already-active mapping only needs setMappingDataAction ON ITS OWN —
//     there is NO setMappingAction, because the mapping (name + type) is already configured. This
//     is used by proofreading split/merge and save/rebase, which recompute the mapping locally and
//     re-publish it. setMappingDataAction therefore never carries a name/type: it always refers to
//     the mapping that setMappingAction configured (phase 1) or that is already active.

export default function* watchActivatedMappings(): Saga<void> {
  const oldActiveMappingByLayer = {
    value: yield* select((state) => state.temporaryConfiguration.activeMappingByLayer),
  };
  // Buffer the activation requests since they might be dispatched before WK_INITIALIZED.
  // Only SET_MAPPING (the activation request, phase 1 of case (1) above) is handled here.
  // SET_MAPPING_DATA (the actual mapping data — phase 2 of an activation, or a standalone data
  // update as in case (2)) is handled by finishMappingActivation below.
  const setMappingActionChannel = yield* actionChannel("SET_MAPPING");
  yield* call(ensureWkInitialized);
  yield* takeLatest(setMappingActionChannel, handleSetMapping, oldActiveMappingByLayer);
  yield* takeLatest("SET_MAPPING_DATA", finishMappingActivation);
  yield* takeEvery(
    "ENSURE_LAYER_MAPPINGS_ARE_LOADED",
    function* handler(action: EnsureLayerMappingsAreLoadedAction) {
      const layerName =
        action.layerName || (yield* select((state) => getVisibleSegmentationLayer(state)?.name));
      const layerMappings = layerName ? yield* loadLayerMappings(layerName, true) : [];
      action.callback(layerMappings != null);
    },
  );
  yield* takeEvery("DEBUG__RELOAD_HDF5_MAPPING", reloadHdf5Mapping);
  const segmentationLayers = yield* select((state) => getSegmentationLayers(state.dataset));
  for (const layer of segmentationLayers) {
    // The following saga will fork internally.
    yield* takeLatestMappingChange(oldActiveMappingByLayer, layer.name);
  }
  // Keep RebaseRelevantAnnotationState updated.
  yield* takeEvery(["SET_MAPPING", "SET_MAPPING_DATA"], keepMappingInfoInUpdated);
}

function* clearActiveMapping(volumeTracingId: string) {
  const newMapping = new Map();

  yield* put(setMappingDataAction(volumeTracingId, newMapping, false));
}

function* reloadHdf5Mapping() {
  /*
   * currently only exists for debugging purposes. can be invoked via
   *   webknossos.DEV.store.dispatch({type: "DEBUG__RELOAD_HDF5_MAPPING"})
   */
  const volumeTracingLayer = yield* select((state) => getActiveSegmentationTracingLayer(state));

  const actionTracingId = volumeTracingLayer?.tracingId;
  if (actionTracingId == null) {
    return;
  }
  const layerName = actionTracingId;
  const mappingInfo = yield* select((state) =>
    getMappingInfo(state.temporaryConfiguration.activeMappingByLayer, layerName),
  );
  const dataset = yield* select((state) => state.dataset);
  const layerInfo = getLayerByName(dataset, layerName);
  const { mappingName } = mappingInfo;

  if (mappingName == null) {
    throw new Error("Could not apply splitAgglomerate because no active mapping was found.");
  }

  yield* call(clearActiveMapping, actionTracingId);
  yield* call(updateLocalHdf5Mapping, layerName, layerInfo, mappingName);
}

const isAgglomerate = (mapping: ActiveMappingInfo) => {
  return mapping.mappingType !== "JSON" && mapping.mappingType != null;
};

function* reloadData(
  oldActiveMappingByLayer: Container<Record<string, ActiveMappingInfo>>,
  action: { layerName: string },
): Saga<void> {
  const { layerName } = action;
  const activeMappingByLayer = yield* select(
    (state) => state.temporaryConfiguration.activeMappingByLayer,
  );
  const mapping = getMappingInfo(activeMappingByLayer, layerName);

  // Especially, when switching between move tool and proofreading tool for the first time
  // a latency is to be expected. Therefore, we want to notify the user about what's happening.
  // After a proofreading action has been made, tool switching won't cause a layer reload.
  message.loading({
    content: "Reloading segmentation data...",
    duration: 1,
  });
  yield* call([api.data, api.data.reloadBuckets], layerName);

  const needsLocalHdf5Mapping = yield* select((state) =>
    getNeedsLocalHdf5Mapping(state, layerName),
  );

  // If an agglomerate mapping is being activated (that is applied remotely), the data
  // reload is the last step of the mapping activation. For JSON mappings or locally applied
  // HDF5 mappings, the last step of the mapping activation is the texture creation in mappings.ts
  if (isAgglomerate(mapping) && !needsLocalHdf5Mapping) {
    if (mapping.mappingStatus === MappingStatusEnum.ACTIVATING) {
      yield* put(finishMappingInitializationAction(layerName));
      yield* put(snapshotMappingDataForNextRebaseAction(layerName));
      message.destroy(MAPPING_MESSAGE_KEY);
    } else if (mapping.mappingStatus === MappingStatusEnum.ENABLED) {
      // If the mapping is already enabled (happens when an annotation was loaded initially
      // with a remotely applied hdf5 mapping), ensure that the message to the user is hidden, too.
      message.destroy(MAPPING_MESSAGE_KEY);
    }
  }

  oldActiveMappingByLayer.value = activeMappingByLayer;
}

function createNeededBucketDataChangedChannel(dataCube: DataCube) {
  return eventChannel((emit) => {
    const neededBucketDataChangedHandler = () => {
      emit("NEEDED_BUCKET_DATA_CHANGED");
    };

    const unbind = dataCube.emitter.on("neededBucketDataChanged", neededBucketDataChangedHandler);
    return unbind;
  }, buffers.sliding<string>(1));
}

function createBucketRetrievalSourceChannel(layerName: string) {
  const getBucketRetrievalSourceForLayer = getBucketRetrievalSourceFn(layerName);
  return eventChannel((emit) => {
    const unbind = listenToStoreProperty(
      (state) => getBucketRetrievalSourceForLayer(state),
      (retrievalSource) => emit(retrievalSource),
    );
    return unbind;
  }, buffers.sliding<BucketRetrievalSource>(1));
}

function* watchChangedBucketsForLayer(layerName: string): Saga<never> {
  /*
   * This saga listens for changed bucket data and then triggers the updateLocalHdf5Mapping
   * saga in an interruptible manner. See comments below for some rationale.
   */
  const dataCube = yield* call([Model, Model.getCubeByLayerName], layerName);
  const bucketChannel = yield* call(createNeededBucketDataChangedChannel, dataCube);

  // Also update the local hdf5 mapping by inspecting all already existing
  // buckets (likely, there are none yet because all buckets were reloaded, but
  // it's still safer to do this here).
  yield* call(startInterruptibleUpdateMapping);

  while (true) {
    yield take(bucketChannel);
    // We received a NEEDED_BUCKET_DATA_CHANGED event. `startInterruptibleUpdateMapping` needs
    // to be invoked.
    // However, let's throttle¹ this by waiting and then discarding all other events
    // that might have accumulated in between.

    yield* call(sleep, BUCKET_WATCHING_THROTTLE_DELAY);
    yield flush(bucketChannel);
    // After flushing and while the startInterruptibleUpdateMapping below is running,
    // the bucketChannel might fill up again. This means, the
    // next loop will immediately take from the channel which
    // is what we need.
    yield* call(startInterruptibleUpdateMapping);

    // Addendum:
    // ¹ We don't use redux-saga's throttle, because that would
    //   call `startInterruptibleUpdateMapping` in parallel if enough events are
    //   consumed over the throttling duration.
    //   However, running `startInterruptibleUpdateMapping` in parallel would be a waste
    //   of computation. Therefore, we invoke `startInterruptibleUpdateMapping` strictly
    //   sequentially.
  }

  function* startInterruptibleUpdateMapping() {
    const dataset = yield* select((state) => state.dataset);
    const layerInfo = getLayerByName(dataset, layerName);
    const mappingInfo = yield* select((state) =>
      getMappingInfo(state.temporaryConfiguration.activeMappingByLayer, layerName),
    );
    const { mappingName, mappingStatus } = mappingInfo;

    if (mappingName == null || mappingStatus !== MappingStatusEnum.ENABLED) {
      return;
    }

    // Updating the HDF5 mapping is an async task which requires communication with
    // the back-end. If the front-end does a proofreading operation in parallel,
    // there is a risk of a race condition. Therefore, we cancel the updateHdf5
    // saga as soon as an operation starts and retry afterwards.
    // Theoretically, the take on REGISTER_OPERATION could be narrowed down
    // so that we only cancel when its a proofreading operation or rebasing operation.
    // However, for now the wide `take` should be okay, too.
    while (true) {
      const isBlocked = yield* select(
        (state) => state.operationContext.activeOperations.length > 0,
      );
      if (!isBlocked) {
        const { cancel } = yield* race({
          updateHdf5: call(updateLocalHdf5Mapping, layerName, layerInfo, mappingName),
          cancel: take("REGISTER_OPERATION"),
        });
        if (!cancel) {
          return;
        }
        console.log("Cancelled updateHdf5");
      }

      yield* waitUntilNoActiveOperations();
      console.log("Retrying updateHdf5...");
    }
  }
}

// Returns null if the mappings could not be loaded (an error toast was already shown in that case).
function* loadLayerMappings(
  layerName: string,
  updateInStore: boolean,
): Saga<[string[], string[]] | null> {
  const dataset = yield* select((state) => state.dataset);
  const layerInfo = getLayerByName(dataset, layerName);

  if (layerInfo.category === "color") {
    throw new Error("loadLayerMappings was called with a color layer.");
  }

  if (layerInfo.mappings != null && layerInfo.agglomerates != null) {
    return [layerInfo.mappings, layerInfo.agglomerates];
  }

  let jsonMappings: string[];
  let serverHdf5Mappings: string[];

  if (layerInfo.tracingId != null && layerInfo.fallbackLayer == null) {
    // The layer is a volume tracing without fallback layer. No mappings
    // exist for these kind of layers and should not be requested from the server.
    jsonMappings = [];
    serverHdf5Mappings = [];
  } else {
    const params = [
      dataset.dataStore.url,
      dataset, // If there is a fallbackLayer, request mappings for that instead of the tracing segmentation layer
      "fallbackLayer" in layerInfo && layerInfo.fallbackLayer != null
        ? layerInfo.fallbackLayer
        : layerInfo.name,
    ] as const;
    const [jsonMappingsResult, serverHdf5MappingsResult] = yield* all([
      call(getMappingsForDatasetLayer, ...params),
      call(getAgglomeratesForDatasetLayer, ...params),
    ]);
    if (!jsonMappingsResult.ok || !serverHdf5MappingsResult.ok) {
      return null;
    }
    jsonMappings = jsonMappingsResult.value;
    serverHdf5Mappings = serverHdf5MappingsResult.value;
  }

  if (updateInStore) {
    yield* put(setLayerMappingsAction(layerName, jsonMappings, serverHdf5Mappings));
  }

  return [jsonMappings, serverHdf5Mappings];
}

function* handleSetMapping(
  oldActiveMappingByLayer: Container<Record<string, ActiveMappingInfo>>,
  action: SetMappingAction,
): Saga<void> {
  const { layerName, mappingName, mappingType, showLoadingIndicator, dataIsProvidedExternally } =
    action;

  // Editable mappings cannot be disabled or switched for now
  const isEditableMappingActivationAllowed = yield* select((state) =>
    isMappingActivationAllowed(state, mappingName, layerName),
  );
  if (!isEditableMappingActivationAllowed) return;

  if (mappingName == null) {
    return;
  }

  if (dataIsProvidedExternally) {
    // The caller supplies the mapping data directly via a follow-up setMappingDataAction (e.g. the
    // front-end API / merger mode), so there is nothing to load from the server here. The
    // SET_MAPPING reducer already configured name/type/status; finishMappingActivation completes
    // the activation once the data action arrives.
    return;
  }

  const visibleSegmentationLayerName = yield* select(
    (state) => getVisibleSegmentationLayer(state)?.name,
  );
  if (showLoadingIndicator && layerName === visibleSegmentationLayerName) {
    // Only show the message if the mapping belongs to the currently visible
    // segmentation layer. Otherwise, the message would stay as long as the
    // actual layer not visible.
    message.loading({
      content: "Activating Mapping",
      key: MAPPING_MESSAGE_KEY,
      duration: 0,
    });
  }

  const layerInfo = yield* select((state) => getLayerByName(state.dataset, layerName));

  const success = yield* call(
    ensureMappingsAreLoadedAndRequestedMappingExists,
    layerInfo,
    mappingName,
    mappingType,
  );
  if (!success) {
    return;
  }

  if (mappingType === "JSON") {
    yield* call(handleSetJsonMapping, layerName, mappingName, mappingType);
  } else {
    // Default to HDF5 mapping which is the default for now. Only JSON mappings must be treated specially.
    yield* call(
      handleSetHdf5Mapping,
      layerName,
      layerInfo,
      mappingName,
      action,
      oldActiveMappingByLayer,
    );
  }
}

function* finishMappingActivation(action: SetMappingDataAction): Saga<void> {
  // Runs on every SET_MAPPING_DATA, i.e. for phase 2 of an activation (case 1) as well as for a
  // standalone data update of an already-active mapping (case 2). The mapping data has already
  // been stored in the store (by the SET_MAPPING_DATA reducer). Here we update
  // the mapping textures (a no-op if the layer's textures have not been set up yet, e.g. in tests
  // without a GPU) and finish the activation (status ACTIVATING -> ENABLED).
  const { layerName, mapping, mappingColors, isMergerModeMapping } = action;

  // The SetMappingDataAction only updates the mapping data. The mapping's name and type are already configured.
  // So we read them from the current active mapping.
  const activeMapping = yield* select((state) =>
    getMappingInfo(state.temporaryConfiguration.activeMappingByLayer, layerName),
  );

  // Mirror the gate of the SET_MAPPING_DATA reducer.
  const isActivationAllowed = yield* select((state) =>
    isMappingActivationAllowed(state, activeMapping.mappingName, layerName, !!isMergerModeMapping),
  );
  if (!isActivationAllowed) {
    return;
  }

  // Only proceed if the reducer actually applied this exact mapping and the activation is still
  // in progress. This skips superseded/rejected actions and avoids double-finishing.
  if (
    activeMapping.mapping !== mapping ||
    activeMapping.mappingStatus !== MappingStatusEnum.ACTIVATING
  ) {
    return;
  }

  // Apply custom colors if they were passed directly (e.g. via the front-end API). For JSON
  // mappings the colors are applied in handleSetJsonMapping instead (using the server-provided
  // class ordering), which is why those dispatches don't carry mappingColors.
  if (mappingColors != null && mappingColors.length > 0) {
    const classes = convertMappingObjectToEquivalenceClasses(mapping);
    yield* call(setCustomColors, action, classes, layerName);
  }

  const mappings = Model.getLayerByName(layerName).mappings;
  if (mappings != null) {
    yield* call([mappings, mappings.updateMappingTextures], mapping);
  }

  yield* put(finishMappingInitializationAction(layerName));
  message.destroy(MAPPING_MESSAGE_KEY);
}

function* handleSetHdf5Mapping(
  layerName: string,
  layerInfo: APIDataLayer,
  mappingName: string,
  action: SetMappingAction,
  oldActiveMappingByLayer: Container<Record<string, ActiveMappingInfo>>,
): Saga<void> {
  if (yield* select((state) => getNeedsLocalHdf5Mapping(state, layerName))) {
    yield* call(updateLocalHdf5Mapping, layerName, layerInfo, mappingName);
  } else {
    // An HDF5 mapping was set that is applied remotely. A reload is necessary.
    yield* call(reloadData, oldActiveMappingByLayer, action);
  }
}

function* updateLocalHdf5Mapping(
  layerName: string,
  layerInfo: APIDataLayer,
  mappingName: string,
): Saga<void> {
  const previousMapping = yield* select(
    (store) => store.temporaryConfiguration.activeMappingByLayer[layerName].mapping,
  );
  const previousMappingOrEmpty = previousMapping || (new Map() as Mapping);

  const isZoomThresholdExceeded = yield* select((state) =>
    isZoomThresholdExceededForAgglomerateMapping(state, layerName),
  );

  if (isZoomThresholdExceeded) {
    // We do not try to look up all segment ids because these are usually too many
    // in coarse magnifications.
    // A toast will be shown to the user in the warnAboutSegmentationZoom saga.
    if (previousMapping == null) {
      // If an annotation with activated mapping is loaded (on page load) with an exceeded zoom threshold,
      // we set an empty mapping object. This needs to be done to let the mapping activation conclude
      // (compare finishMappingActivation in mapping_saga.ts).
      // In cases where a mapping is already set and the zoom threshold is exceeded, it is not changed
      // to avoid useless updates to the cuckoo textures. Once the threshold is no longer exceeded, the mapping
      // will be updated again.
      yield* put(setMappingDataAction(layerName, new Map(), true));
    }
    return;
  }

  const cube = Model.getCubeByLayerName(layerName);
  const segmentIds = cube.getValueSetForAllNeededBuckets();

  const {
    aWithoutB: newSegmentIds,
    bWithoutA: deletedValues,
    // The `intersection` value returned by diffSetAndMap
    // is a fresh Map instance which is why we may mutate
    // that without any problems. This is done later to
    // avoid duplicating data for performance reasons.
    intersection: mutableRemainingEntries,
  } = fastDiffSetAndMap(segmentIds as Set<NumberLike>, previousMappingOrEmpty);

  if (previousMapping != null && newSegmentIds.size === 0 && deletedValues.size === 0) {
    // The set of rendered segment ids is exactly the one the current mapping was built for.
    // We can skip updating the mapping.
    yield* call(adaptActiveSegmentToProofreadingMarker, layerName);
    return;
  }

  let newEntries;
  try {
    newEntries = yield* call(
      getAgglomeratesForSegmentIds,
      layerName,
      layerInfo,
      mappingName,
      newSegmentIds,
    );
  } catch (exception) {
    console.error("Could not load agglomerate ids for segments due to", exception);
    Toast.error(
      "Could not load agglomerate ids for segments. Some segments will remain hidden for now.",
    );
    return;
  }

  // It is safe to mutate mutableRemainingEntries to compute the merged,
  // new mapping. See the definition of mutableRemainingEntries.
  const mapping = mutableRemainingEntries as Mapping;
  for (const [key, val] of newEntries.entries()) {
    // @ts-expect-error
    mapping.set(key, val);
  }

  setCacheResultForDiffMappings(previousMappingOrEmpty, mapping, {
    changed: [],
    onlyA: deletedValues,
    onlyB: newSegmentIds,
  });

  yield* put(setMappingDataAction(layerName, mapping, true));

  yield* call(adaptActiveSegmentToProofreadingMarker, layerName);
}

export async function fetchAgglomeratesForSegmentIds(
  dataset: APIDataset,
  annotation: StoreAnnotation,
  editableMapping: EditableMapping | null | undefined,
  layerName: string,
  layerInfo: APIDataLayer,
  mappingName: string,
  newSegmentIds: Set<NumberLike>,
): Promise<Mapping> {
  // If there is a fallbackLayer, request mappings for that instead of the tracing segmentation layer
  const mappingLayerName =
    "fallbackLayer" in layerInfo && layerInfo.fallbackLayer != null
      ? layerInfo.fallbackLayer
      : layerName;

  return editableMapping != null
    ? getAgglomeratesForSegmentsFromTracingstore(
        annotation.tracingStore.url,
        editableMapping.tracingId,
        newSegmentIds,
        annotation.annotationId,
        annotation.version,
      )
    : getAgglomeratesForSegmentsFromDatastore(
        dataset.dataStore.url,
        dataset,
        mappingLayerName,
        mappingName,
        newSegmentIds,
      );
}

export function* getAgglomeratesForSegmentIds(
  layerName: string,
  layerInfo: APIDataLayer,
  mappingName: string,
  newSegmentIds: Set<NumberLike>,
) {
  const dataset = yield* select((state) => state.dataset);
  const annotation = yield* select((state) => state.annotation);
  const editableMapping = yield* select((state) =>
    getEditableMappingForVolumeTracingId(state, layerName),
  );
  return yield* call(() =>
    fetchAgglomeratesForSegmentIds(
      dataset,
      annotation,
      editableMapping,
      layerName,
      layerInfo,
      mappingName,
      newSegmentIds,
    ),
  );
}

function* adaptActiveSegmentToProofreadingMarker(layerName: string) {
  const annotation = yield* select((state) => state.annotation);

  const volumeTracing = getVolumeTracingByLayerName(annotation, layerName);
  if (!volumeTracing) {
    return;
  }

  const activeTool = yield* select((state) => state.uiInformation.activeTool);

  if (activeTool !== AnnotationTool.PROOFREAD) {
    return;
  }

  const proofreadingMarkerPosition = yield* select(
    (state) => state.localSegmentationStateByLayer[layerName]?.proofreadingMarkerPosition,
  );
  if (proofreadingMarkerPosition) {
    const agglomerateId = yield* call(getSegmentIdForPositionAsync, proofreadingMarkerPosition);
    const activeSegmentId = yield* call(getActiveCellId, volumeTracing);

    if (activeSegmentId !== agglomerateId) {
      yield put(setActiveCellAction(agglomerateId, proofreadingMarkerPosition));
      // TODO #9456: Look for a solution that this toast notification does not appear frequently in live collab scenario.
      // yield* call(() =>
      //   Toast.info(
      //     `The active segment id was automatically changed from ${activeSegmentId} to ${agglomerateId}, because the agglomerate id at the proofreading marker changed.`,
      //   ),
      // );
      console.log(
        `The active segment id was automatically changed from ${activeSegmentId} to ${agglomerateId}, because the agglomerate id at the proofreading marker changed.`,
      );
    }
  }
}

function* handleSetJsonMapping(
  layerName: string,
  mappingName: string,
  mappingType: MappingType,
): Saga<void> {
  console.time("MappingSaga JSON");
  const fetchedMappings: APIMappings = {};
  const result = yield* call(fetchMappings, layerName, mappingName, fetchedMappings);
  if (!result.ok) {
    yield* call(
      [Toast, Toast.error],
      "The requested mapping could not be loaded.",
      { sticky: true },
      result.error.message,
    );
    console.error(result.error.cause);
    message.destroy(MAPPING_MESSAGE_KEY);
    yield* put(setMappingAction(layerName, null, mappingType, false));
    return;
  }
  const fetchedMapping = fetchedMappings[mappingName];
  const { hideUnmappedIds, colors: mappingColors } = fetchedMapping;

  const mapping = yield* call(buildMappingObject, mappingName, fetchedMappings);
  const mappingProperties = {
    mapping,
    mappingColors,
    hideUnmappedIds,
  };

  const usesCustomColors = mappingColors != null && mappingColors.length > 0;
  if (usesCustomColors) {
    yield* call(setCustomColors, mappingProperties, fetchedMapping.classes || [], layerName);
  }

  console.timeEnd("MappingSaga JSON");
  // The custom colors (if any) were already applied above, so they are not passed again here
  // (finishMappingActivation would otherwise re-apply them based on a possibly different class
  // ordering).
  yield* put(setMappingDataAction(layerName, mapping, false, { hideUnmappedIds }));
}

function convertMappingObjectToEquivalenceClasses(existingMapping: Mapping) {
  const classesByRepresentative: Record<string, NumberLike[]> = {};
  for (let [unmapped, mapped] of existingMapping.entries()) {
    // Regardless of number or bigint, convert to string so that it can be used as a
    // key in both cases.
    const mappedStr = mapped.toString();
    classesByRepresentative[mappedStr] = classesByRepresentative[mappedStr] || [];
    classesByRepresentative[mappedStr].push(unmapped);
  }
  const classes = Object.values(classesByRepresentative);
  return classes;
}

function* setCustomColors(
  mappingProperties: OptionalMappingProperties,
  classes: NumberLike[][],
  layerName: string,
) {
  if (mappingProperties.mapping == null || mappingProperties.mappingColors == null) {
    return;
  }
  let classIdx = 0;
  for (const aClass of classes) {
    const firstIdEntry = aClass[0];
    if (firstIdEntry == null) continue;

    const representativeId = (mappingProperties.mapping as NumberLikeMap).get(firstIdEntry);
    if (representativeId == null) continue;

    const hueValue = mappingProperties.mappingColors[classIdx];
    const color = jsHsv2rgb(360 * hueValue, 1, 1);
    yield* put(updateSegmentAction(toBigInt(representativeId), { color }, layerName));

    classIdx++;
  }
}

function* fetchMappings(
  layerName: string,
  mappingName: string,
  fetchedMappings: APIMappings,
): Saga<ApiResult<void>> {
  const dataset = yield* select((state) => state.dataset);
  const layerInfo = getLayerByName(dataset, layerName);
  // If there is a fallbackLayer, request mappings for that instead of the tracing segmentation layer
  const mappingLayerName =
    "fallbackLayer" in layerInfo && layerInfo.fallbackLayer != null
      ? layerInfo.fallbackLayer
      : layerName;
  const result = yield* call(
    fetchMapping,
    dataset.dataStore.url,
    dataset,
    mappingLayerName,
    mappingName,
    // The caller shows a more specific error toast.
    { showErrorToast: false },
  );
  if (!result.ok) {
    return result;
  }
  const mapping = result.value;
  fetchedMappings[mappingName] = mapping;

  if (mapping.parent != null) {
    return yield* call(fetchMappings, layerName, mapping.parent, fetchedMappings);
  }
  return { ok: true, value: undefined };
}

function buildMappingObject(mappingName: string, fetchedMappings: APIMappings): Mapping {
  const mappingObject = new Map<number, number>();

  for (const currentMappingName of getMappingChain(mappingName, fetchedMappings)) {
    const mapping = fetchedMappings[currentMappingName];
    ErrorHandling.assertExists(
      mapping.classes,
      "Mappings must have been fetched at this point. Ensure that the mapping JSON contains a classes property.",
    );

    for (const mappingClass of mapping.classes) {
      const minId = min(mappingClass);
      if (minId == null) {
        // The class is empty and can be ignored
        continue;
      }
      const mappedId = mappingObject.get(minId) || minId;

      for (const id of mappingClass) {
        mappingObject.set(id, mappedId);
      }
    }
  }

  return mappingObject;
}

function getMappingChain(mappingName: string, fetchedMappings: APIMappings): Array<string> {
  const chain = [mappingName];
  const mapping = fetchedMappings[mappingName];
  const parentMappingName = mapping.parent;

  if (parentMappingName != null) {
    return chain.concat(getMappingChain(parentMappingName, fetchedMappings));
  }

  return chain;
}

function* ensureMappingsAreLoadedAndRequestedMappingExists(
  layerInfo: APIDataLayer,
  mappingName: string,
  mappingType: MappingType,
) {
  const { name: layerName } = layerInfo;
  // Make sure the available mappings are persisted in the store if they are not already
  const areServerHdf5MappingsInStore =
    "agglomerates" in layerInfo && layerInfo.agglomerates != null;
  const layerMappings = yield* call(loadLayerMappings, layerName, !areServerHdf5MappingsInStore);
  if (layerMappings == null) {
    message.destroy(MAPPING_MESSAGE_KEY);
    yield* put(setMappingAction(layerName, null, mappingType, true, {}));
    return false;
  }
  const [jsonMappings, serverHdf5Mappings] = layerMappings;

  const editableMappings = yield* select((state) =>
    state.annotation.volumes
      .filter((volumeTracing) => volumeTracing.hasEditableMapping)
      .map((volumeTracing) => volumeTracing.mappingName),
  );
  const hdf5Mappings = [...serverHdf5Mappings, ...editableMappings];
  const mappingsWithCorrectType = mappingType === "JSON" ? jsonMappings : hdf5Mappings;

  if (!mappingsWithCorrectType.includes(mappingName)) {
    // Mapping does not exist, set mappingName back to null
    const availableMappings = mappingsWithCorrectType.join(",");
    const availableMappingsString =
      availableMappings.length > 0
        ? `Available ${mappingType} mappings are ${availableMappings}`
        : `There are no available ${mappingType} mappings`;
    const errorMessage = `Mapping with name ${mappingName} and type ${mappingType} does not exist. ${availableMappingsString}.`;
    message.error({
      content: errorMessage,
      key: MAPPING_MESSAGE_KEY,
      duration: 10,
    });
    console.error(errorMessage);
    yield* put(setMappingAction(layerName, null, mappingType, true, {}));

    return false;
  }

  return true;
}

// On every SET_MAPPING / SET_MAPPING_DATA make sure that, in case it only updates the mapping with
// info already stored on the server, the RebaseRelevantAnnotationState is updated as well.
function* keepMappingInfoInUpdated(action: SetMappingAction | SetMappingDataAction) {
  if (action.isVersionStoredOnServer) {
    yield put(snapshotMappingDataForNextRebaseAction(action.layerName));
  }
}
